import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from "react-router-dom";
import {getApp, likeApp, unlikeApp, isLikedApp, downloadApp, listPublicAppFiles} from "../../API/APP/AppAPI";
import { Card, CardContent, CardHeader, Typography, Button, Grid, Box, Chip, CardMedia, IconButton } from '@mui/material';
import DownloadForOfflineOutlinedIcon from '@mui/icons-material/DownloadForOfflineOutlined';
import { KBImageComponent } from "../KBImage.js";
import { FavoriteBorderOutlined, Favorite, School } from "@mui/icons-material";
import { isAutoinstall, isMobile, isWordpress } from "../../App/utils";
import { formatNumber, getCategoryIcon } from "../utils";
import { BackButton } from "../BackButton";
import LocalLoader from "../../Loaders/LocalLoader";
import {RAW_FILES_URL} from "../../utils";
import {ScreenshotsCarousel} from "./Carousel";
import {AppInstallStepper} from "./InstallStepper";

function format(date) {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
}

const autoinstall = isAutoinstall() || isWordpress();

export const AppInstall = ({userProfile}) => {
    const [app, setApp] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isLiked, setIsLiked] = useState(false);
    const { appId } = useParams();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const publishedKbId = searchParams.get('publishedKbId');
    const publishedTitle = searchParams.get('publishedTitle');
    // For published KBs, start installation immediately (activeStep=0)
    const [activeStep, setActiveStep] = useState(publishedKbId ? 0 : -1);

    useEffect(() => {
        const fetchApp = async () => {
            setIsLoading(true);
            try {
                // If it's a published KB, create minimal app data
                if (publishedKbId) {
                    setApp({
                        appId: publishedKbId,
                        kbTitle: publishedTitle || 'My Project',
                        kbDescription: '',
                        kbInstructions: '',
                        screenshots: [],
                        likes: 0,
                        downloads: 0,
                        updatedAt: Date.now(),
                        category: 'general'
                    });
                } else {
                    const data = await getApp(appId);
                    const files = autoinstall ? [] : (await listPublicAppFiles(appId, 'screenshots'));
                    const screenshots = files.filter(o => !o.endsWith('/')).map(o => `${RAW_FILES_URL}/${o}`)

                    setApp({ ...data, screenshots });

                    const likedStatus = autoinstall ? false : (await isLikedApp(appId));
                    setIsLiked(likedStatus.isLiked);
                }
            } catch (error) {
                console.error('Error fetching app:', error);
            }
            setIsLoading(false);
        };

        fetchApp();
    }, [appId, publishedKbId]);

    useEffect(() => {
        if (app === null) return;
        if (autoinstall
            // && userProfile && [PHONE_VERIFICATION_STATUS.VERIFIED, PHONE_VERIFICATION_STATUS.CANCELED]
            //     .includes(userProfile?.phoneVerificationStatus)
        ) {
            installApp();
        }
    }, [app, userProfile]);

    // console.log(JSON.stringify(app))

    const handleLike = async () => {
        if (isLiked) {
            await unlikeApp(appId);
            setIsLiked(false);
            setApp({ ...app, likes: app.likes - 1 });
        } else {
            await likeApp(appId);
            setIsLiked(true);
            setApp({ ...app, likes: app.likes + 1 });
        }
    };

    const installApp = async () => setActiveStep(0)

    if (isLoading || !app) {
        return <LocalLoader open={true} blocking={true} />;
    }

    const formattedUpdatedAt = format(new Date(parseInt(app.updatedAt)));
    const carouselStyle = { marginTop: '24px', marginBottom: !isMobile ? '48px' : undefined };
    const marginTop = isMobile ? undefined : '24px';

    if (activeStep > -1) {
        return <AppInstallStepper {...{app, appId, activeStep, setActiveStep, userProfile, publishedKbId}} />
    }

    return (
        <Grid container spacing={2} justifyContent="center" >
            <Grid item xs={12} md={8} lg={10}>
                <Box mt={2} display="flex" justifyContent="flex-start">
                    <BackButton path='/app-center' />
                </Box>
                <Card>
                    <CardHeader
                        avatar={
                            <CardMedia component={KBImageComponent} height="120px" width="120px" folder={'app-image'}
                                alt={app.kbTitle}
                                id={app.appId}
                                style={{width: '100%', height: '100%', objectFit: 'contain', borderRadius: '4px'}}
                            />
                        }
                        title={
                            <div>
                                <Typography variant={isMobile ? 'h6' : 'h5'} component="div">
                                    {app.kbTitle?.split(':')[0]}
                                </Typography>
                            </div>
                        }
                        action={
                            !isMobile ? <Chip
                                icon={getCategoryIcon(app.category)}
                                label={app.category}
                                style={{textTransform: 'capitalize'}}
                            /> : null
                        }
                        style={{ paddingBottom: 0, alignItems: 'start' }}
                    />

                    <CardContent>
                        <Typography gutterBottom variant="body1" color="text.secondary" align="left" style={{ marginTop: '16px' }}>
                            {app.kbDescription}
                        </Typography>

                        {isMobile && (
                            <Box display="flex" alignItems="center" gap={1} my={2} style={{ marginTop }} >
                                {app.category === 'education' && <School />}
                                <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                                    {app.category}
                                </Typography>
                            </Box>
                        )}

                        <Typography variant="subtitle1" color="textSecondary" gutterBottom align="left" style={{ marginTop }}>
                            Updated on: {formattedUpdatedAt}
                        </Typography>

                        <Box display="flex" alignItems="center" gap={1} my={2}>
                            <IconButton onClick={handleLike} color="primary">
                                {isLiked ? <Favorite /> : <FavoriteBorderOutlined />}
                            </IconButton>
                            <Typography variant="body2" style={{ marginLeft: '-8px' }}>{formatNumber(app.likes)}</Typography>
                            <DownloadForOfflineOutlinedIcon />
                            <Typography variant="body2">{formatNumber(app.downloads)}</Typography>
                        </Box>
                        
                        <Box display="flex" alignItems="center" style={{ width: isMobile ? '100%' : '300px', justifyContent: 'flex-start', marginTop: '24px' }}> {/* Adjusted width and alignment */}
                            <Button onClick={installApp} variant="contained" color="secondary" style={{ width: '100%' }}>
                                Deploy Agent
                            </Button>

                            {/*<Button onClick={installApp} variant="outlined" color="secondary" style={{ width: '100%', marginLeft: '8px' }}>*/}
                            {/*    Clone & Evolve*/}
                            {/*</Button>*/}
                        </Box>

                        {app.screenshots?.length > 0 && <>
                            {/*<Typography variant={isMobile ? 'h6' : 'h5'} component="h2" style={carouselStyle}>Screenshots</Typography>*/}
                            {app.screenshots && <ScreenshotsCarousel screenshots={app.screenshots} />}
                        </>}

                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};